import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

interface ContractSection {
    label: string;
    items: ContractItem[];
}

interface ContractItem {
    type: 'text' | 'field';
    label?: string;
    content?: string;
    value?: string;
    fillable?: boolean;
}

const CreateContract: React.FC = () => {
    const [templates, setTemplates] = useState<any[]>([]);
    const [clients, setClients] = useState<any[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
    const [selectedClientUserId, setSelectedClientUserId] = useState<number | null>(null);
    const [contractName, setContractName] = useState('');
    const [contractId, setContractId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isClientsLoaded, setIsClientsLoaded] = useState(false);
    const [error, setError] = useState('');
    const [templateSections, setTemplateSections] = useState<ContractSection[]>([]);
    const [fillableFields, setFillableFields] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        const loadData = async () => {
            await fetchTemplates();
            await fetchClients();
            setIsClientsLoaded(true);
        };
        loadData();
    }, []);

    useEffect(() => {
        if (!isClientsLoaded || !selectedClientId) {
            setSelectedClientUserId(null);
            return;
        }

        const selectedClient = clients.find(client => client.id === selectedClientId);
        if (selectedClient && selectedClient.clientUserId) { // Updated to clientUserId
            setSelectedClientUserId(selectedClient.clientUserId);
        } else {
            setSelectedClientUserId(null);
        }
    }, [selectedClientId, clients, isClientsLoaded]);

    useEffect(() => {
        if (selectedTemplateId) {
            fetchTemplateDetails();
        } else {
            setTemplateSections([]);
            setFillableFields({});
        }
    }, [selectedTemplateId]);

    const fetchTemplates = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/ContractTemplates`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setTemplates(response.data);
        } catch (error: any) {
            setError('Failed to load contract templates. Please try again.');
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Clients`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setClients(response.data);
        } catch (error: any) {
            setError('Failed to load clients. Please try again.');
        }
    };

    const fetchTemplateDetails = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/ContractTemplates/${selectedTemplateId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const parsedData = JSON.parse(response.data.jsonContent || '{"sections": []}');
            setTemplateSections(parsedData.sections || []);
            const initialFields: { [key: string]: string } = {};
            parsedData.sections.forEach((section: ContractSection, sectionIndex: number) => {
                section.items.forEach((item: ContractItem, itemIndex: number) => {
                    if (item.fillable) {
                        initialFields[`${sectionIndex}-${itemIndex}`] = item.value || '';
                    }
                });
            });
            setFillableFields(initialFields);
        } catch (error: any) {
            setError('Failed to load template details.');
        }
    };

    const updateFillableField = (key: string, value: string) => {
        setFillableFields({ ...fillableFields, [key]: value });
    };

    const createContract = async () => {
        if (!selectedTemplateId || !contractName.trim()) {
            setError('Please select a template and enter a contract name.');
            return;
        }
        if (selectedClientId && !selectedClientUserId) {
            setError('Selected client has no associated user account. Please create one first.');
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            const token = localStorage.getItem('token');
            const updatedSections = templateSections.map((section, sectionIndex) => ({
                ...section,
                items: section.items.map((item, itemIndex) => {
                    if (item.fillable) {
                        const key = `${sectionIndex}-${itemIndex}`;
                        return { ...item, value: fillableFields[key] || item.value };
                    }
                    return item;
                }),
            }));
            const payload = {
                contractTemplateId: selectedTemplateId,
                name: contractName,
                clientId: selectedClientId,
                clientUserId: selectedClientUserId,
                jsonData: JSON.stringify({ sections: updatedSections }),
            };
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/Contracts`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setContractId(response.data.id);
            setContractName('');
            setSelectedTemplateId(null);
            setSelectedClientId(null);
            setSelectedClientUserId(null);
            setTemplateSections([]);
            setFillableFields({});
            alert('Contract created successfully!');
        } catch (error: any) {
            console.error('Failed to create contract:', error);
            setError(error.response?.data || 'Failed to create contract. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container mt-5">
            <h2 className="mb-4 text-center">Create Contract</h2>
            <div className="card shadow-sm mb-4">
                <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                    <h4 className="mb-0">Select a Template</h4>
                    <Link to="/contract-templates" className="btn btn-light btn-sm">New Template</Link>
                </div>
                <div className="card-body">
                    {error && <div className="alert alert-danger">{error}</div>}
                    <div className="mb-3">
                        <label className="form-label">Contract Name</label>
                        <input
                            type="text"
                            className="form-control"
                            value={contractName}
                            onChange={(e) => setContractName(e.target.value)}
                            disabled={isLoading}
                        />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">Client (Optional)</label>
                        <select
                            className="form-control"
                            value={selectedClientId || ''}
                            onChange={(e) => setSelectedClientId(e.target.value ? parseInt(e.target.value) : null)}
                            disabled={isLoading}
                        >
                            <option value="">No client</option>
                            {clients.map(client => (
                                <option key={client.id} value={client.id}>{client.name}</option>
                            ))}
                        </select>
                    </div>
                    {templates.length === 0 ? (
                        <div className="alert alert-warning text-center">
                            No contract templates found. Create a new template to get started.
                        </div>
                    ) : (
                        <ul className="list-group list-group-flush">
                            {templates.map(template => (
                                <li key={template.id} className="list-group-item d-flex justify-content-between align-items-center">
                                    <span>{template.name}</span>
                                    <button
                                        onClick={() => setSelectedTemplateId(template.id)}
                                        className={`btn btn-sm ${selectedTemplateId === template.id ? 'btn-primary' : 'btn-outline-primary'}`}
                                        disabled={isLoading}
                                    >
                                        {selectedTemplateId === template.id ? 'Selected' : 'Select'}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    {templateSections.length > 0 && (
                        <div className="mt-4">
                            <h4>Fill in Contract Details</h4>
                            {templateSections.map((section, sectionIndex) => (
                                <div key={sectionIndex} className="card mb-3">
                                    <div className="card-body">
                                        <h5 className="text-primary">{section.label}</h5>
                                        <div className="border rounded p-3 bg-light">
                                            {section.items.map((item, itemIndex) => (
                                                <div key={itemIndex} className="mb-2">
                                                    {item.type === 'text' && <p className="mb-0">{item.content}</p>}
                                                    {item.type === 'field' && (
                                                        <div>
                                                            <strong>{item.label}:</strong>{' '}
                                                            {item.fillable ? (
                                                                <input
                                                                    type="text"
                                                                    className="form-control d-inline-block"
                                                                    style={{ width: 'auto', display: 'inline-block' }}
                                                                    value={fillableFields[`${sectionIndex}-${itemIndex}`] || ''}
                                                                    onChange={(e) => updateFillableField(`${sectionIndex}-${itemIndex}`, e.target.value)}
                                                                    disabled={isLoading}
                                                                />
                                                            ) : (
                                                                <span>{item.value || <span className="text-muted">Not filled</span>}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="text-center mt-4">
                        <button onClick={createContract} className="btn btn-primary" disabled={isLoading}>
                            {isLoading ? 'Creating...' : 'Create Contract'}
                        </button>
                    </div>
                    {contractId && (
                        <div className="alert alert-success text-center mt-3">
                            Contract created! ID: {contractId}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreateContract;